import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { apiFetch } from "../services/api";
import { entityAPI } from "../services/entityCache";
import { createDevConsole } from "../utils/devLogger";

const console = createDevConsole();

// Reusable entity autocomplete component
// Props:
// - value: string (controlled input value)
// - onChange: (newValue: string) => void
// - onSelect: (entity) => void (entity object {id,name,type,description,...})
// - placeholder?: string
// - minChars?: number (default 1)
// - maxResults?: number (default 10)
// - inputClassName?: string
// - containerClassName?: string
// - dropdownClassName?: string
// - disabled?: boolean
// - onEnter?: (currentValue: string) => void
// - inputRef?: React.RefObject<HTMLInputElement> (optional ref to the input element)
const EntityAutocomplete = ({
  value,
  onChange,
  onSelect,
  placeholder = "Search entities…",
  minChars = 1,
  maxResults = 10,
  inputClassName = "",
  containerClassName = "",
  dropdownClassName = "",
  disabled = false,
  onEnter,
  inputRef,
}) => {
  const [allEntities, setAllEntities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const containerRef = useRef(null);
  const loadedRef = useRef(false);
  const [renderAbove, setRenderAbove] = useState(false);
  const currentTenantRef = useRef(null);

  // Get current tenant ID from user object
  const getCurrentTenantId = useCallback(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      return user?.organization_id || user?.tenant_id || null;
    } catch (e) {
      return null;
    }
  }, []);

  // Load entities once (cached) for fast client-side filtering
  // Always reload if tenant changes to ensure tenant isolation
  const ensureLoaded = useCallback(async () => {
    const currentTenant = getCurrentTenantId();
    
    // If tenant changed, reset cache
    if (currentTenantRef.current !== null && currentTenantRef.current !== currentTenant) {
      console.log("🔍 EntityAutocomplete: Tenant changed, resetting cache");
      loadedRef.current = false;
      setAllEntities([]);
    }
    
    if (loadedRef.current || loading) return;
    
    currentTenantRef.current = currentTenant;
    setLoading(true);
    try {
      console.log("🔍 EntityAutocomplete: Loading entities from server (tenant:", currentTenant, ")...");
      // Use search endpoint with empty query to get all entities - this ensures tenant filtering
      // The search endpoint is more reliable for tenant isolation
      const entities = await entityAPI.getEntitiesForAutocomplete("");
      setAllEntities(entities);
      loadedRef.current = true;
      console.log(
        "🔍 EntityAutocomplete: Loaded",
        entities.length,
        "entities from server (tenant:",
        currentTenant,
        ")"
      );
    } catch (e) {
      console.error("EntityAutocomplete: Failed to load entities:", e);
      // Fallback to regular getEntities if search fails
      try {
        const entities = await entityAPI.getEntities();
        setAllEntities(entities);
        loadedRef.current = true;
        console.log("🔍 EntityAutocomplete: Fallback loaded", entities.length, "entities");
      } catch (fallbackError) {
        console.error("EntityAutocomplete: Fallback also failed:", fallbackError);
        setAllEntities([]);
        loadedRef.current = true;
      }
    } finally {
      setLoading(false);
    }
  }, [loading, getCurrentTenantId]);

  useEffect(() => {
    // Load lazily on first focus/open
    if (open) ensureLoaded();
  }, [open, ensureLoaded]);

  // Auto placement: render dropdown above if not enough space below
  useEffect(() => {
    if (!open) return;
    const computePlacement = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const estimatedHeight = 220; // approx dropdown height
      setRenderAbove(spaceBelow < estimatedHeight);
    };
    computePlacement();
    window.addEventListener("resize", computePlacement);
    window.addEventListener("scroll", computePlacement, true);
    return () => {
      window.removeEventListener("resize", computePlacement);
      window.removeEventListener("scroll", computePlacement, true);
    };
  }, [open]);

  // Filter results
  const results = useMemo(() => {
    const raw = value || "";
    const match = raw.match(/([A-Za-z0-9_]+)$/); // last token
    const term = match ? match[1].toLowerCase() : "";
    if (!term || term.length < minChars) return [];
    const isInterfaceType = (t) =>
      (t || "").toLowerCase().includes("interface");
    // Filter then sort: non-interfaces first, interfaces last; keep stable name ordering
    return allEntities
      .filter(
        (e) =>
          (e.name || "").toLowerCase().includes(term) ||
          (e.type || "").toLowerCase().includes(term)
      )
      .sort((a, b) => {
        const aIsInt = isInterfaceType(a.type);
        const bIsInt = isInterfaceType(b.type);
        if (aIsInt !== bIsInt) return aIsInt ? 1 : -1; // interfaces last
        // Secondary: prioritize name match strength (startsWith over contains)
        const aName = (a.name || "").toLowerCase();
        const bName = (b.name || "").toLowerCase();
        const aStarts = aName.startsWith(term) ? 0 : 1;
        const bStarts = bName.startsWith(term) ? 0 : 1;
        if (aStarts !== bStarts) return aStarts - bStarts;
        return aName.localeCompare(bName);
      })
      .slice(0, maxResults);
  }, [value, allEntities, minChars, maxResults]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      if (!open || results.length === 0) return;
      e.preventDefault();
      setHighlightIndex((prev) => (prev + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      if (!open || results.length === 0) return;
      e.preventDefault();
      setHighlightIndex((prev) => (prev - 1 + results.length) % results.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (
        open &&
        results.length > 0 &&
        highlightIndex >= 0 &&
        highlightIndex < results.length
      ) {
        const chosen = results[highlightIndex];
        onSelect?.(chosen);
        setOpen(false);
        setHighlightIndex(-1);
      } else if (onEnter) {
        onEnter(value || "");
      }
    } else if (e.key === "Escape") {
      setOpen(false);
      setHighlightIndex(-1);
    }
  };

  // Check if inputClassName contains "query-textarea" to use textarea instead of input
  const isTextarea = inputClassName.includes("query-textarea");
  const InputComponent = isTextarea ? "textarea" : "input";
  const inputProps = isTextarea ? { rows: 3 } : { type: "text" };

  return (
    <div ref={containerRef} className={`relative ${containerClassName}`}>
      <InputComponent
        ref={inputRef}
        {...inputProps}
        value={value}
        onChange={(e) => {
          onChange?.(e.target.value);
          setOpen(true);
          // kick off loading when user starts typing
          ensureLoaded();
          setHighlightIndex(-1);
        }}
        onFocus={() => {
          setOpen(true);
          ensureLoaded();
        }}
        onKeyDown={(e) => {
          // Handle Enter key for textarea
          if (isTextarea && e.key === "Enter" && !e.shiftKey) {
            if (onEnter) {
              onEnter(e);
            }
          } else {
            handleKeyDown(e);
          }
        }}
        placeholder={placeholder}
        className={`w-full ${inputClassName}`}
        disabled={disabled}
        autoComplete="off"
        style={isTextarea ? { resize: "vertical", minHeight: "3rem", maxHeight: "8rem", overflowY: "auto" } : {}}
      />

      {open &&
        (((value || "").match(/([A-Za-z0-9_]+)$/)?.[1]?.length || 0) >=
          minChars ||
          loading) && (
          <div
            className={`absolute ${renderAbove ? "bottom-full mb-1" : "top-full mt-1"} left-0 right-0 bg-gray-800 border border-gray-700 rounded-lg shadow-xl max-h-64 overflow-y-auto ${dropdownClassName}`}
            style={{ 
              position: "absolute",
              zIndex: 9999,
              // Ensure dropdown appears above other content
              boxShadow: "0 10px 25px rgba(0, 0, 0, 0.5)",
              // Ensure it's visible and not clipped
              isolation: "isolate"
            }}
          >
            {loading && (
              <div className="p-2 text-sm text-gray-300">Loading…</div>
            )}
            {!loading && results.length === 0 && (
              <div className="p-2 text-sm text-gray-400">No matches</div>
            )}
            {!loading && results.length > 0 && (
              <div className="py-1">
                {results.slice(0, Math.max(5, maxResults)).map((entity, idx) => (
                  <div
                    key={entity.id}
                    onMouseDown={(e) => {
                      // use onMouseDown to avoid blur before click
                      e.preventDefault();
                      onSelect?.(entity);
                      setOpen(false);
                      setHighlightIndex(-1);
                    }}
                    onMouseEnter={() => setHighlightIndex(idx)}
                    className={`px-3 py-2.5 cursor-pointer text-sm flex items-center justify-between ${
                      highlightIndex === idx
                        ? "bg-gray-700 text-white"
                        : "text-gray-200 hover:bg-gray-700"
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-white truncate">{entity.name}</div>
                      <div className="text-xs text-gray-400 truncate mt-0.5">
                        {entity.type}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
    </div>
  );
};

export default EntityAutocomplete;
